// @ts-check
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { useImmer } from 'use-immer'
import { VirtualPageElement, VirtualPagesElement, VirtualPagesScrollerElement } from './Styled'

const PAGE_PADDING_TOP = 'env(safe-area-inset-top, 0px)'
const PAGE_PADDING_BOTTOM = 'env(safe-area-inset-bottom, 0px)'

const PAGE_GAP = CSS.supports('width', 'max(0px,0px)')
  ? 'max(var(--page-padding-top), var(--page-padding-bottom))'
  : 'calc(var(--page-padding-top), var(--page-padding-bottom))'

const BASE_OFFSET = CSS.supports('width', 'max(0px,0px)') ? `max(0px, calc(${PAGE_GAP} - ${PAGE_PADDING_TOP}))` : '0px'

/**
 * [---------] [--------]
 * [page top ] [        ]
 * [---------] [        ]
 * [page     ] [ screen ]
 * [         ] [        ]
 * [         ] [        ]
 * [---------] [        ]
 * [page gap ] [        ] [--------]
 * [         ] [        ] [        ]
 * [---------] [--------] [        ]
 * [page     ]            [ screen ]
 * [         ]            [        ]
 * [         ]            [        ]
 * [---------]            [        ]
 * [page bot ]            [        ]
 * [         ]            [        ]
 * [---------]            [--------]
 */

type State = 'INITIAL' | 'HOLD' | 'MOVE' | 'ANIMATE' | 'LOCKED'
type Position = [x: number, y: number]

const MIN_MOVE_DISTANCE = 20
const TIME_WINDOW_MS = 500
const MIN_SPEED = 100
const ANIMATE_TIMEOUT = 500

const C = {
  '+': (...args: string[]) => `calc(${args.join(' + ')})`,
  '-': (a: string, b: string) => `calc(${a} - ${b})`,
  '*': (a: string, b: string) => `calc(${a} * ${b})`,
  '/': (a: string, b: string) => `calc(${a} / ${b})`,
}

interface VirtualPagesProps {
  onNext?: () => void
  onPrev?: () => void
  onScrollPast50?: (targetPage: 'prev' | 'next') => void
  children: React.ReactNode
}

export const VirtualPages = forwardRef(({ onNext, onPrev, onScrollPast50, children }: VirtualPagesProps, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const hasReportedPast50Ref = useRef(false)
  const reportedDirectionRef = useRef<'up' | 'down' | null>(null)

  const [pageState, setPageState] = useImmer({
    state: 'INITIAL' as State,
    initialPosition: [0, 0] as [number, number],
    /** -1 ~ 1 */
    pageIndex: 0,
    /** px (number)*/
    pageOffset: 0,
    trace: [] as [y: number, timeMs: number][],
    capturedPointer: -1,
    animateDeadline: 0,
  })

  const pageWrapClassName = useMemo(() => {
    if (pageState.state === 'MOVE') {
      return 'short-video-virtual-page virtual-page-animating'
    }
    return 'short-video-virtual-page'
  }, [pageState])

  const resetPageOffset = () => {
    setPageState((arg) => {
      arg.state = 'INITIAL'
      arg.initialPosition = [0, 0]
      arg.pageIndex = 0
      arg.pageOffset = 0
      arg.trace = []
    })
  }

  useImperativeHandle(ref, () => ({ resetPageOffset }))

  const childrenArray = children as React.ReactNode[]
  const prevPage = (childrenArray as any)[0]
  const currentPage = (childrenArray as any)[1] ?? <VirtualPage />
  const nextPage = (childrenArray as any)[2]

  const totalPage = (prevPage ? 1 : 0) + 1 + (nextPage ? 1 : 0)
  // const initialTranslate = prevPage != null ? 1 : 0
  const PAGE_HEIGHT_TO_BASE = C['-'](C['-']('100%', PAGE_PADDING_TOP), PAGE_PADDING_BOTTOM)
  const SCROLLER_HEIGHT = C['+'](
    PAGE_PADDING_TOP,
    C['*'](PAGE_HEIGHT_TO_BASE, String(totalPage)),
    C['*'](PAGE_GAP, String(totalPage - 1)),
    PAGE_PADDING_BOTTOM
  )
  const PAGE_HEIGHT_TO_SCROLLER = C['/'](
    C['-'](C['-'](C['-']('100%', PAGE_PADDING_TOP), PAGE_PADDING_BOTTOM), C['*'](PAGE_GAP, String(totalPage - 1))),
    String(totalPage)
  )
  const BASE_POSITION = prevPage == null ? '0px' : C['+'](PAGE_PADDING_TOP, PAGE_HEIGHT_TO_SCROLLER, BASE_OFFSET)
  const TRANSFORM = `translateY(${C['*'](
    C['+'](
      BASE_POSITION,
      C['*'](C['+'](PAGE_HEIGHT_TO_SCROLLER, PAGE_GAP), String(pageState.pageIndex)),
      pageState.pageOffset + 'px'
    ),
    '-1'
  )})`

  const transform = TRANSFORM
  const scrollerHeight = SCROLLER_HEIGHT
  const pageHeight = PAGE_HEIGHT_TO_SCROLLER

  const onPointerDown = (ev: React.SyntheticEvent<HTMLDivElement, PointerEvent>) => {
    const currentPos: Position = [ev.nativeEvent.clientX, ev.nativeEvent.clientY]

    // prevent animate on user are pressing in player control bar
    let el: HTMLElement | null = ev.target as HTMLElement
    while (el) {
      if (el.className.includes('block-virtual-page')) {
        return
      }
      el = el.parentElement
    }

    if (pageState.state === 'INITIAL') {
      setPageState((arg) => {
        arg.state = 'HOLD'
        arg.initialPosition = currentPos
      })
    }
    // force update if missed for whatever reason
    if (pageState.state === 'HOLD') {
      setPageState((arg) => {
        arg.state = 'HOLD'
        arg.initialPosition = currentPos
      })
    }

    if (pageState.state === 'ANIMATE' && pageState.animateDeadline < Date.now()) {
      // force cancel animation
      scrollerRef.current?.classList.remove('has-transition')
      setPageState((arg) => {
        arg.state = 'HOLD'
        arg.initialPosition = currentPos
      })
    }
  }

  const onPointerMove = (ev: React.SyntheticEvent<HTMLDivElement, PointerEvent>) => {
    const currentPos: Position = [ev.nativeEvent.clientX, ev.nativeEvent.clientY]
    const initialPos = pageState.initialPosition

    if (
      (ev.target as HTMLElement).querySelector(':scope .vjs-seek-speed-up')
      // && pageState.state === 'HOLD'
    ) {
      // 已經進行影片快轉，取消上下滑動行為
      setPageState((arg) => {
        arg.state = 'INITIAL'
      })
      return
    }

    if (pageState.state === 'HOLD') {
      if (Math.abs(currentPos[1] - initialPos[1]) > MIN_MOVE_DISTANCE) {
        containerRef.current?.setPointerCapture(ev.nativeEvent.pointerId)
        setPageState((arg) => {
          arg.state = 'MOVE'
          arg.initialPosition = currentPos
          arg.trace.push([currentPos[1], Date.now()])
          arg.capturedPointer = ev.nativeEvent.pointerId
        })
      }
    }

    if (pageState.state === 'MOVE') {
      const offset = -(currentPos[1] - initialPos[1])

      const now = Date.now()

      // 偵測是否滑動超過 50%，回傳給父層
      const height = containerRef.current?.getBoundingClientRect().height ?? 0
      const threshold = height * 0.5
      const isPast50 = Math.abs(offset) > threshold
      const direction = offset < 0 ? 'up' : 'down'
      const targetPage = offset < 0 ? 'prev' : 'next'

      if (isPast50 && !hasReportedPast50Ref.current) {
        hasReportedPast50Ref.current = true
        reportedDirectionRef.current = direction as 'up' | 'down'
        onScrollPast50?.(targetPage as 'prev' | 'next')
      }

      setPageState((arg) => {
        arg.pageOffset = offset
        arg.trace.push([currentPos[1], Date.now()])
        arg.trace = arg.trace.filter((i) => now - i[1] < TIME_WINDOW_MS)
      })
    }
  }

  const onPointerCancel = (ev: React.SyntheticEvent<HTMLDivElement, PointerEvent>) => {
    hasReportedPast50Ref.current = false
    reportedDirectionRef.current = null
    if (pageState.state === 'HOLD') {
      setPageState((arg) => {
        arg.state = 'INITIAL'
        arg.pageIndex = 0
        arg.pageOffset = 0
        arg.trace = []
      })
    }
    if (pageState.state === 'MOVE') {
      containerRef.current?.releasePointerCapture(pageState.capturedPointer)
      scrollerRef.current?.classList.add('has-transition')
      setPageState((arg) => {
        arg.state = 'ANIMATE'
        arg.pageIndex = 0
        arg.pageOffset = 0
        arg.trace = []
      })
    }
  }

  const onPointerUp = (ev: React.SyntheticEvent<HTMLDivElement, PointerEvent>) => {
    hasReportedPast50Ref.current = false
    reportedDirectionRef.current = null
    if (pageState.state === 'HOLD') {
      setPageState((arg) => {
        arg.state = 'INITIAL'
        arg.pageIndex = 0
        arg.pageOffset = 0
        arg.trace = []
      })
    }
    if (pageState.state === 'MOVE') {
      const height = containerRef.current?.getBoundingClientRect().height ?? 0
      containerRef.current?.releasePointerCapture(pageState.capturedPointer)
      if (Math.abs(pageState.pageOffset) > height * 0.5) {
        scrollerRef.current?.classList.add('has-transition')
        setPageState((arg) => {
          arg.state = 'ANIMATE'
          arg.pageIndex = pageState.pageOffset < 0 ? -1 : 1
          arg.pageOffset = 0
          arg.trace = []
        })

        return
      }
      // check speed
      const now = Date.now()
      const trace = pageState.trace.filter((i) => now - i[1] < TIME_WINDOW_MS)
      const start = trace[0]
      const end = trace[trace.length - 1]
      const speed = start == null || end == null ? 0 : (end[0] - start[0]) / ((end[1] - start[1]) / 1000)

      if (Math.abs(speed) > MIN_SPEED) {
        scrollerRef.current?.classList.add('has-transition')
        setPageState((arg) => {
          arg.state = 'ANIMATE'
          arg.pageIndex = speed > 0 ? -1 : 1
          arg.pageOffset = 0
          arg.trace = []
          arg.animateDeadline = Date.now() + ANIMATE_TIMEOUT
        })
        return
      }

      // else, just cancel
      if (pageState.pageOffset !== 0) {
        scrollerRef.current?.classList.add('has-transition')
        setPageState((arg) => {
          arg.state = 'ANIMATE'
          arg.pageIndex = 0
          arg.pageOffset = 0
          arg.trace = []
          arg.animateDeadline = Date.now() + ANIMATE_TIMEOUT
        })
      } else {
        setPageState((arg) => {
          arg.state = 'INITIAL'
          arg.pageIndex = 0
          arg.pageOffset = 0
          arg.trace = []
        })
      }
    }
  }

  const onTransitionEnd = (ev: React.TransitionEvent<HTMLDivElement>) => {
    // 只處理 scroller 的 transform transition，避免子元素 transitionend 冒泡造成重複觸發
    if (ev.target !== scrollerRef.current || !['transform', '-webkit-transform'].includes(ev.propertyName)) {
      return
    }
    if (pageState.state === 'ANIMATE') {
      scrollerRef.current?.classList.remove('has-transition')
      if (pageState.pageIndex === 0) {
        resetPageOffset()
      } else {
        setPageState((arg) => {
          arg.state = 'LOCKED'
        })
        if (pageState.pageIndex > 0) {
          onNext?.()
        }
        if (pageState.pageIndex < 0) {
          onPrev?.()
        }
      }
    }
  }

  return (
    <VirtualPagesElement
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onTransitionEnd={onTransitionEnd}
      className={pageWrapClassName}
      style={
        {
          '--scroller-height': scrollerHeight,
          '--page-height': pageHeight,
          '--page-gep': PAGE_GAP,
          '--page-padding-top': PAGE_PADDING_TOP,
          '--page-padding-bottom': PAGE_PADDING_BOTTOM,
        } as React.CSSProperties
      }
    >
      {/** wrapper */}
      <VirtualPagesScrollerElement
        ref={scrollerRef}
        style={{
          transform,
        }}
      >
        {[
          ...(nextPage ? [<div key="padding-top" style={{ height: PAGE_PADDING_TOP }} />] : []),
          ...(prevPage ? [<prevPage.type key={prevPage.key} {...prevPage.props} />] : []),
          <div key="page_top_2" style={{ height: prevPage ? PAGE_GAP : PAGE_PADDING_TOP }} />,
          <currentPage.type key={currentPage.key} {...currentPage.props} />,
          <div key="page_bottom_2" style={{ height: nextPage ? PAGE_GAP : PAGE_PADDING_BOTTOM }} />,
          <nextPage.type key={nextPage.key} {...nextPage.props} />,
          ...(nextPage ? [<div key="padding-bottom" style={{ height: PAGE_PADDING_BOTTOM }} />] : []),
        ]}
      </VirtualPagesScrollerElement>
    </VirtualPagesElement>
  )
})

interface VirtualPageProps {
  children?: React.ReactNode
  [key: string]: any
}

export const VirtualPage = ({ children = null, ...props }: VirtualPageProps) => {
  return <VirtualPageElement {...props}>{children}</VirtualPageElement>
}
